import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Users, Inbox, Brain, FileText, CornerDownLeft, X } from "lucide-react";
import api from "@/lib/api";

const norm = (s) => (s || "").toString().toLowerCase();

export default function GlobalSearch({ canOperate, canPending }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ people: [], pending: [], knowledge: [], docs: [] });
  const inputRef = useRef(null);

  // global ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((v) => !v); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 60);
    const jobs = [];
    if (canOperate) {
      jobs.push(api.get("/employees").then((r) => ["people", r.data]).catch(() => ["people", []]));
      jobs.push(api.get("/knowledge?status=current").then((r) => ["knowledge", r.data]).catch(() => ["knowledge", []]));
      jobs.push(api.get("/documents").then((r) => ["docs", r.data]).catch(() => ["docs", []]));
    }
    if (canPending) jobs.push(api.get("/pending").then((r) => ["pending", r.data]).catch(() => ["pending", []]));
    Promise.all(jobs).then((res) => {
      const next = { people: [], pending: [], knowledge: [], docs: [] };
      res.forEach(([k, v]) => { next[k] = Array.isArray(v) ? v : []; });
      setData(next);
    });
  }, [open, canOperate, canPending]);

  const go = (to) => { setOpen(false); setQ(""); nav(to); };
  const ql = norm(q);
  const match = (item, fields) => !ql || fields.some((f) => norm(f).includes(ql));

  const groups = [
    {
      key: "people", label: "Pessoas", icon: Users, to: "/app/team",
      items: data.people.filter((e) => match(e, [e.name, e.department, e.email, e.phone]))
        .slice(0, 6).map((e) => ({ id: e.id, title: e.name, sub: e.department || e.role })),
    },
    {
      key: "pending", label: "Pendências", icon: Inbox, to: "/app/pending",
      items: data.pending.filter((p) => match(p, [p.question, p.employee_name, p.reason]))
        .slice(0, 6).map((p) => ({ id: p.id, title: p.question || p.employee_name || "Pendência", sub: p.employee_name })),
    },
    {
      key: "knowledge", label: "Conhecimento", icon: Brain, to: "/app/brain",
      items: data.knowledge.filter((k) => match(k, [k.title, k.content, k.text]))
        .slice(0, 6).map((k) => ({ id: k.id, title: k.title || (k.content || k.text || "").slice(0, 60), sub: k.scope })),
    },
    {
      key: "docs", label: "Documentos", icon: FileText, to: "/app/brain",
      items: data.docs.filter((d) => match(d, [d.filename, d.title, d.name]))
        .slice(0, 5).map((d) => ({ id: d.id, title: d.filename || d.title || d.name || "Documento", sub: d.status })),
    },
  ].filter((g) => g.items.length);

  return (
    <>
      <button data-testid="global-search-trigger" onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50/70 px-4 py-2 text-sm text-slate-400 transition-colors duration-200 hover:border-slate-300 hover:text-slate-600">
        <Search size={16} strokeWidth={2} />
        <span className="hidden lg:inline">Buscar…</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} data-testid="global-search-modal"
            className="fixed inset-0 z-[80] flex items-start justify-center bg-slate-900/30 px-4 pt-[12vh] backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, y: -14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_30px_80px_-20px_rgba(11,18,32,0.35)]">
              <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3.5">
                <Search size={18} className="text-slate-400" />
                <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input"
                  placeholder="Buscar pessoas, pendências, conhecimento, documentos…"
                  className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none" />
                <button onClick={() => setOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
              </div>
              <div className="max-h-[52vh] overflow-y-auto p-2">
                {!groups.length && (
                  <p className="px-3 py-8 text-center text-sm text-slate-400">
                    {q ? "Nada encontrado." : "Comece a digitar para buscar em todo o sistema."}
                  </p>
                )}
                {groups.map((g) => (
                  <div key={g.key} className="mb-1">
                    <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g.label}</p>
                    {g.items.map((it) => (
                      <button key={it.id} onClick={() => go(g.to)} data-testid={`search-result-${g.key}`}
                        className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-slate-50">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900/[0.04] text-slate-600"><g.icon size={15} /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-slate-900">{it.title}</span>
                          {it.sub && <span className="block truncate text-xs text-slate-400">{it.sub}</span>}
                        </span>
                        <CornerDownLeft size={14} className="text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
